<template lang="pug">
u-main
  u-container
    u-page
      u-page-header(
        title="Terms of Service"
        headline="Last updated: May 2026"
      )
      u-page-body
        article.flex.flex-col.gap-8.text-sm.text-muted.leading-relaxed
          p.text-base.text-default
            | These Terms of Service (“Terms”) govern your access to and use of the website
            |
            u-link(
              href="https://unattended.company"
              external
            ) unattended.company
            |
            | and related online presence operated by Unattended Oy (“Unattended Company,” “we,” “us,” or “our”). They also set out general terms applicable to mobile applications we publish through the Apple App Store and Google Play Store, unless a specific product provides more detailed terms. By using this website or our apps, you agree to be bound by these Terms. If you do not agree, do not use the website or our apps.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 1. Provider
            p
              | The website and our apps are provided by Unattended Oy, a company registered in Finland under Business ID 3622216-6. You can reach us at
              |
              u-link(href="mailto:frank@unattended.company") frank@unattended.company
              | .

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 2. Scope
            p
              | These Terms apply to your use of unattended.company, including any content, materials, or features we make available on the site. Individual products and services may have additional or more specific terms; where those terms conflict with these Terms on a matter specific to that product, the product terms control for that product.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 3. Our Apps
            p
              | We develop and publish mobile applications. Each app may have its own terms of service, privacy policy, and support information. For example, Poemly is governed by the
              |
              u-link(
                href="https://poemly.app/terms"
                target="_blank"
                external
              ) Poemly Terms of Service
              |
              | and related policies on poemly.app. You should read the applicable product terms before creating an account, starting a trial, or making a purchase in any app.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 4. Eligibility
            p
              | You must be at least 13 years old to use this website. By using the website, you represent that you meet this requirement and that you have the legal capacity to enter into these Terms. Some of our apps may impose additional age or eligibility requirements as described in their product terms.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 5. Acceptable Use
            p You agree not to:
            ul(class="list-disc pl-5 flex flex-col gap-1.5")
              li Use the website in any unlawful manner or for any unlawful purpose
              li Attempt to gain unauthorized access to our systems, accounts, or networks
              li Interfere with or disrupt the website or its security features
              li Use automated means to scrape, harvest, or extract content from the website without our prior written consent
              li Misrepresent your affiliation with Unattended Oy or impersonate any person or entity
              li Upload or transmit malware, spam, or other harmful or disruptive material

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 6. Intellectual Property
            p
              | The website, our name, logos, product names, graphics, text, software, and other materials are owned by or licensed to Unattended Oy and are protected by copyright, trademark, and other intellectual property laws. We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the website for your personal, non-commercial purposes in accordance with these Terms. You may not copy, modify, distribute, sell, or create derivative works from our materials except as expressly permitted by us or by applicable law.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 7. Third-Party Links and Services
            p
              | The website may link to third-party sites, app stores, or services (including Apple, Google, and our product websites). We do not control and are not responsible for third-party content, policies, or practices. Your use of third-party services is subject to their terms and policies.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 8. Consumer Rights (EU/EEA &amp; UK)
            p
              | Nothing in these Terms limits or excludes any non-waivable statutory rights you have as a consumer under the laws of your country of residence. If you purchase digital content or subscriptions through our apps, additional consumer rights and withdrawal rules may apply as described in the relevant product terms and store policies. EU consumers may use the European Commission’s Online Dispute Resolution platform at
              |
              u-link(
                href="https://ec.europa.eu/consumers/odr"
                target="_blank"
                external
              ) ec.europa.eu/consumers/odr
              | .

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 9. Intellectual Property Complaints
            p
              | If you believe that content on this website or in one of our apps infringes your rights, contact
              |
              u-link(href="mailto:frank@unattended.company") frank@unattended.company
              |
              | with:
            ul(class="list-disc pl-5 flex flex-col gap-1.5")
              li A description of the content concerned and where it appears
              li The basis of your claim and the jurisdiction(s) in which you assert rights
              li Reasonable evidence that you are the rights holder or are authorized to act on their behalf
              li Your contact information
            p
              | We will review valid notices promptly and take appropriate action, including removal or restriction of content where warranted, in line with applicable law including the EU Digital Services Act where it applies.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 10. Privacy
            p
              | Our collection and use of personal data is described in our
              |
              u-link(to="/privacy") Privacy Policy
              | . By using the website, you acknowledge that you have read that policy.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 11. Disclaimers
            p
              | To the maximum extent permitted by law, the website is provided “as is” and “as available,” without warranties of any kind, whether express or implied. We do not warrant that the website will be uninterrupted, error-free, or secure. This section does not limit any non-waivable statutory consumer rights described in Section 8.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 12. Limitation of Liability
            p
              | To the maximum extent permitted by law, Unattended Oy and its affiliates, officers, employees, and agents will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenues, data, or goodwill, arising out of or related to your use of the website. Our total aggregate liability for any claim arising out of or related to the website will not exceed EUR 50. Nothing in this section limits liability that cannot be limited under applicable law, including liability for death, personal injury, gross negligence, or wilful misconduct. Claims relating to paid app subscriptions or in-app purchases are subject to the limitations and remedies set out in the applicable product terms and store policies.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 13. Changes to the Website and Terms
            p
              | We may modify or discontinue the website, in whole or in part, at any time. We may also update these Terms from time to time. When we do, we will revise the “Last updated” date above and, where the changes are material, provide reasonable notice on the website. Your continued use of the website after changes take effect constitutes your acceptance of the revised Terms.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 14. Governing Law &amp; Jurisdiction
            p
              | These Terms are governed by the laws of Finland, excluding its conflict-of-laws rules. The courts of Helsinki, Finland have exclusive jurisdiction over any dispute arising out of or relating to these Terms or the website, except that if you are a consumer, you may also bring proceedings in the courts of your country of residence where mandatory local law gives you that right.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 15. Apple App Store Additional Terms
            p
              | If you access any of our applications through the Apple App Store, you acknowledge that these Terms are between you and Unattended Oy only, and not with Apple Inc. Apple is not responsible for our applications or their content. Apple has no obligation to provide maintenance or support for our applications. In the event of any failure of an application to conform to any applicable warranty, you may notify Apple, and Apple will refund the purchase price (if any) for the application; to the maximum extent permitted by law, Apple has no other warranty obligation with respect to our applications. Apple and its subsidiaries are third-party beneficiaries of these Terms and may enforce them against you. Product-specific subscription, billing, and cancellation terms may also apply as set out in the relevant app terms.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 16. Google Play Additional Terms
            p
              | If you access any of our applications through the Google Play Store, you acknowledge that these Terms are between you and Unattended Oy only, and not with Google. Your use of the application is also subject to the Google Play Terms of Service. Google is not responsible for our applications, their content, or support.

          section.flex.flex-col.gap-3
            h2.text-lg.font-semibold.text-default 17. Contact
            p
              | For questions about these Terms, the website, or our apps, contact Unattended Oy at
              |
              u-link(href="mailto:frank@unattended.company") frank@unattended.company
              | .
</template>

<script>
export default {
  name: 'PageTerms'
}
</script>
